/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle, CheckCircle2 } from 'lucide-react';

import SEO from '@/components/SEO';
import LibraryPageLayout from '@/components/library/LibraryPageLayout';
import LibraryStructuredData from '@/components/library/LibraryStructuredData';
import { guideSeoFor } from '@/data/guideSeo';
import {
  LibraryHero,
  SectionHeader,
  FeatureCheckItem,
  LibraryFAQ,
  LibraryFinalCta,
  GuideMeta,
  OfficialSources,
  relatedGuides,
} from '@/components/library/sections';

/**
 * Generic guide page renderer used by every educational guide
 * of the Bibliothèque HÉRITA.
 *
 * Each rendered section receives a stable id so the auto-built table of
 * contents (GuideMeta) can scroll the user to it. Section ids are derived
 * from the guide slug to remain unique even when several guides coexist
 * on the same page during HMR.
 */
const DEFAULT_LAST_UPDATED = '18 février 2026';

const GuidePage = ({ config }) => {
  const seoExtras = guideSeoFor(config.path);
  const sources = config.sources || seoExtras.sources;
  const howTo = config.howTo || seoExtras.howTo;

  const breadcrumb = [
    { label: 'Accueil', to: '/' },
    { label: 'Guides', to: '/comprendre-document-notarie' },
    { label: config.breadcrumbLabel || config.slug },
  ];

  // Build the table of contents dynamically based on which optional sections
  // are present in the config. Anchor ids are simple, stable, French slugs.
  const tocSections = [
    { id: 'definition', label: 'Définition', present: !!config.definition },
    { id: 'utilite', label: 'Utilité', present: !!config.usage },
    { id: 'contenu', label: 'Contenu', present: !!config.content },
    { id: 'vigilance', label: 'Vigilance', present: !!config.checks },
    { id: 'analyse', label: 'Analyse HÉRITA', present: !!config.autoChecks },
    { id: 'exemple', label: 'Exemple', present: !!config.example },
    { id: 'faq', label: 'FAQ', present: true },
  ].filter((s) => s.present);

  return (
    <>
      <SEO
        title={config.seoTitle}
        description={config.seoDescription}
        path={config.path}
        ogTitle={config.ogTitle || config.seoTitle.replace(' | HÉRITA', ' — Guides HÉRITA')}
        ogDescription={config.seoDescription}
      />
      <LibraryStructuredData
        path={config.path}
        title={config.seoTitle}
        description={config.seoDescription}
        breadcrumb={breadcrumb}
        faq={config.faq}
        howTo={howTo}
        datePublished="2026-02-18"
        dateModified="2026-02-18"
      />

      <LibraryPageLayout
        breadcrumb={breadcrumb}
        testId={`page-${config.slug}`}
      >
        {/* HERO */}
        <LibraryHero
          eyebrow={config.hero.eyebrow}
          title={config.hero.title}
          subtitle={config.hero.subtitle}
          trustItems={[
            'Analyse gratuite au départ',
            'Aucun compte requis',
            'Documents supprimés après analyse',
          ]}
        />

        {/* META — reading time, last update, table of contents */}
        <GuideMeta
          readingTime={config.readingTime || '7 min'}
          lastUpdated={config.lastUpdated || DEFAULT_LAST_UPDATED}
          sections={tocSections}
        />

        {/* DEFINITION */}
        {config.definition && (
          <section
            id="definition"
            className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          >
            <div className="max-w-3xl mx-auto">
              <SectionHeader
                eyebrow={config.definition.eyebrow}
                title={config.definition.title}
              />
              <div className="font-['Work_Sans'] text-[15.5px] sm:text-[16px] text-[#2C363F] leading-[1.85] space-y-6">
                {config.definition.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* USAGE */}
        {config.usage && (
          <section
            id="utilite"
            className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 scroll-mt-24"
          >
            <div className="max-w-4xl mx-auto">
              <SectionHeader eyebrow={config.usage.eyebrow} title={config.usage.title} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {config.usage.items.map(({ title, description }) => (
                  <article
                    key={title}
                    className="bg-white border border-[#E2DBCF] rounded-2xl p-7"
                  >
                    <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[24px] text-[#1A2530] mb-3 leading-tight">
                      {title}
                    </h3>
                    <p className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                      {description}
                    </p>
                  </article>
                ))}
              </div>
              {config.usage.contextLink && (
                <p className="mt-10 font-['Work_Sans'] text-[14px] text-[#2C363F] text-center max-w-2xl mx-auto leading-relaxed">
                  {config.usage.contextLink}
                </p>
              )}
            </div>
          </section>
        )}

        {/* CONTENT CARDS */}
        {config.content && (
          <section
            id="contenu"
            className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          >
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow={config.content.eyebrow}
                title={config.content.title}
                intro={config.content.intro}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {config.content.cards.map(({ Icon, title, description }) => (
                  <article
                    key={title}
                    className="bg-[#F9F7F3] border border-[#E2DBCF] rounded-2xl p-6 hover:shadow-md hover:border-[#D3CCBE] transition-all"
                  >
                    <div className="w-11 h-11 rounded-full bg-[#E3D5CA] flex items-center justify-center mb-5">
                      <Icon className="h-5 w-5 text-[#1A2530]" aria-hidden="true" />
                    </div>
                    <h3 className="font-['Cormorant_Garamond'] font-medium text-[20px] sm:text-[22px] text-[#1A2530] mb-2 leading-tight">
                      {title}
                    </h3>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#2C363F] leading-relaxed">
                      {description}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CHECKS / VIGILANCE */}
        {config.checks && (
          <section
            id="vigilance"
            className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 scroll-mt-24"
          >
            <div className="max-w-3xl mx-auto">
              <SectionHeader
                eyebrow={config.checks.eyebrow}
                title={config.checks.title}
                intro={config.checks.intro}
              />
              <ul className="space-y-3 list-none p-0">
                {config.checks.items.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 bg-white border border-[#E2DBCF] rounded-xl p-5"
                  >
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-[#E3D5CA] flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-3.5 w-3.5 text-[#1A2530]" aria-hidden="true" />
                    </span>
                    <span className="font-['Work_Sans'] text-[14.5px] text-[#1A2530] leading-snug">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* AUTO CHECKS */}
        <section
          id="analyse"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
        >
          <div className="max-w-5xl mx-auto">
            <SectionHeader
              eyebrow={config.autoChecks.eyebrow}
              title={config.autoChecks.title}
              intro={config.autoChecks.intro}
            />
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 list-none p-0">
              {config.autoChecks.items.map((label) => (
                <FeatureCheckItem key={label} label={label} />
              ))}
            </ul>
          </div>
        </section>

        {/* EXAMPLE */}
        {config.example && (
          <section
            id="exemple"
            className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 scroll-mt-24"
          >
            <div className="max-w-6xl mx-auto">
              <SectionHeader
                eyebrow={config.example.eyebrow}
                title={config.example.title}
                intro={config.example.intro}
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                <article className="bg-[#F9F7F3] border border-[#E2DBCF] rounded-2xl p-7 sm:p-9 font-['Cormorant_Garamond'] text-[#1A2530]">
                  <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#2C363F] opacity-80 mb-4">
                    Extrait du document (fictif)
                  </p>
                  <p className="text-[16px] sm:text-[17px] leading-[1.85] italic">
                    {config.example.extract}
                  </p>
                </article>
                <article className="bg-white border-t-2 border-t-[#1A2530] border-x border-b border-[#E2DBCF] rounded-2xl p-7 sm:p-9 shadow-md">
                  <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#1A2530] mb-4">
                    Lecture HÉRITA
                  </p>
                  <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] mb-5 leading-tight">
                    {config.example.headline}
                  </h3>
                  <ul className="space-y-3.5 list-none p-0">
                    {config.example.bullets.map((line, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1A2530] flex-shrink-0" />
                        <span className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#1A2530] leading-snug">
                          <strong className="font-semibold">{line.strong}</strong>{line.rest}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 pt-5 border-t border-[#E2DBCF] font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                    Lecture réalisée en moins de 30 secondes
                  </p>
                </article>
              </div>
              <p className="mt-8 font-['Work_Sans'] text-[12.5px] text-[#2C363F] italic text-center opacity-80">
                Exemple illustratif. Tous les noms, montants et adresses sont
                inventés et ne correspondent à aucun dossier réel.
              </p>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section
          id="faq"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
        >
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              eyebrow="Questions fréquentes"
              title="Tout ce que vous voulez savoir"
              intro="Dix questions concrètes pour comprendre ce document sans détour."
            />
            <LibraryFAQ items={config.faq} defaultOpen={0} />
          </div>
        </section>

        {/* FINAL CTA */}
        <LibraryFinalCta
          title={config.finalCta.title}
          subtitle={config.finalCta.subtitle}
        />

        {/* OFFICIAL SOURCES */}
        <OfficialSources sources={sources} />

        {/* DISCOVER */}
        <section className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-t border-[#E2DBCF]">
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Aller plus loin"
              title="Guides associés"
              intro="Les autres guides HÉRITA pour comprendre les actes notariés associés à ce document."
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {(config.discover || relatedGuides(config.path)).map(({ label, to, tag }) => (
                <Link
                  key={to}
                  to={to}
                  className="group block bg-[#F9F7F3] border border-[#E2DBCF] rounded-xl p-5 hover:border-[#1A2530] hover:bg-white transition-all"
                  data-testid={`more-link-${to}`}
                >
                  <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-70 mb-2">
                    {tag || 'Comprendre'}
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-[19px] sm:text-[21px] text-[#1A2530] leading-tight mb-3 font-medium">
                    {label}
                  </p>
                  <span className="inline-flex items-center font-['Work_Sans'] text-[12.5px] font-medium text-[#1A2530] group-hover:underline underline-offset-[5px] decoration-[#1A2530]">
                    Lire le guide
                    <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </LibraryPageLayout>
    </>
  );
};

export default GuidePage;
